// useSpeechRecognition.ts — thin hook around Web Speech API for dictating
// questions in Italian (it-IT). Designed for short bursts, not continuous
// transcription: we set continuous=false so the browser auto-stops after a
// natural pause, which matches "dictate one question, then send".
//
// Support notes (2026-04): Chrome desktop + Android ✓. Safari 14.5+ iOS ✓ (but
// iOS requires a live user gesture each start() and only fires final results —
// interim results are unreliable). Firefox desktop ✗ (no implementation).
// `supported: false` hides the mic button entirely in consumers; we never
// render a greyed-out or confusing UI.

import { useEffect, useRef, useState } from 'react';

// ----- Ambient types ---------------------------------------------------------
// lib.dom.d.ts ships the inner result types (SpeechRecognitionResult, …) but
// omits the top-level SpeechRecognition interface + vendor constructors and
// event shapes. Declared minimally here so TS can typecheck without pulling in
// an extra @types package.

interface SpeechRecognitionEventLike {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEventLike {
  readonly error: string;
  readonly message?: string;
}

interface SpeechRecognitionLike extends EventTarget {
  lang:             string;
  continuous:       boolean;
  interimResults:   boolean;
  maxAlternatives?: number;
  onresult:         ((e: SpeechRecognitionEventLike) => void) | null;
  onerror:          ((e: SpeechRecognitionErrorEventLike) => void) | null;
  onend:            ((e: Event) => void) | null;
  onstart?:         ((e: Event) => void) | null;
  start: () => void;
  stop:  () => void;
  abort: () => void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

declare global {
  interface Window {
    SpeechRecognition?:       SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

// ----- Error mapping ---------------------------------------------------------

export type SpeechErrCode =
  | 'not-allowed'        // user denied mic permission
  | 'service-not-allowed'// browser/system denied (corp policy, insecure origin)
  | 'audio-capture'      // no microphone hardware
  | 'network'            // Chrome routes via Google server; needs online
  | 'no-speech'          // nothing heard in the timeout window
  | 'aborted'            // stopped before a result (often user-initiated)
  | 'unknown';

export interface SpeechErr {
  code:    SpeechErrCode;
  message: string; // Italian, user-facing
}

function mapError(raw: string): SpeechErr {
  switch (raw) {
    case 'not-allowed':
    case 'service-not-allowed':
      return {
        code: raw,
        message: 'Per usare la voce devi autorizzare il microfono nelle impostazioni del browser.',
      };
    case 'audio-capture':
      return {
        code: 'audio-capture',
        message: 'Non rilevo un microfono collegato. Controlla le impostazioni audio.',
      };
    case 'network':
      return {
        code: 'network',
        message: 'La dettatura richiede una connessione a internet. Riprova quando sei online.',
      };
    case 'no-speech':
      return { code: 'no-speech', message: 'Non ho sentito niente. Riprova quando sei pronto.' };
    case 'aborted':
      return { code: 'aborted', message: '' };
    default:
      return { code: 'unknown', message: 'Qualcosa non ha funzionato con la dettatura. Riprova.' };
  }
}

// ----- Hook ------------------------------------------------------------------

export interface UseSpeechRecognitionOptions {
  lang?:            string;
  onInterimResult?: (transcript: string) => void;
  onFinalResult?:   (transcript: string) => void;
}

export interface UseSpeechRecognitionReturn {
  supported:  boolean;
  listening:  boolean;
  error:      SpeechErr | null;
  start:      () => void;
  stop:       () => void;
  clearError: () => void;
}

function getCtor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

export function useSpeechRecognition(
  opts: UseSpeechRecognitionOptions = {},
): UseSpeechRecognitionReturn {
  const { lang = 'it-IT', onInterimResult, onFinalResult } = opts;

  const [supported] = useState<boolean>(() => getCtor() !== null);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<SpeechErr | null>(null);

  const recRef = useRef<SpeechRecognitionLike | null>(null);
  // Keep callbacks in refs so changing them between renders doesn't force us to
  // rebuild the recognition instance (which would lose in-flight audio).
  const onInterimRef = useRef(onInterimResult);
  const onFinalRef   = useRef(onFinalResult);
  useEffect(() => { onInterimRef.current = onInterimResult; }, [onInterimResult]);
  useEffect(() => { onFinalRef.current = onFinalResult; },     [onFinalResult]);

  useEffect(() => {
    const Ctor = getCtor();
    if (!Ctor) return;

    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = false;      // auto-stop after a natural silence
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      // Each event may contain multiple results; the ones at index >= resultIndex
      // are the new ones since the last event. Collect interim + final separately.
      let interim = '';
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const r = e.results[i];
        if (!r || r.length === 0) continue;
        const text = r[0]?.transcript ?? '';
        if (r.isFinal) final += text;
        else           interim += text;
      }
      if (interim) onInterimRef.current?.(interim);
      if (final)   onFinalRef.current?.(final);
    };

    rec.onerror = (e) => {
      const mapped = mapError(e.error);
      // 'aborted' is usually user-initiated (clicked mic again) and 'no-speech'
      // just means the auto-timeout fired — we don't want a scary message for
      // either. Still stop listening.
      if (mapped.code !== 'aborted' && mapped.code !== 'no-speech') {
        setError(mapped);
      }
    };

    rec.onend = () => {
      setListening(false);
    };

    recRef.current = rec;
    return () => {
      try { rec.abort(); } catch { /* noop */ }
      recRef.current = null;
    };
  }, [lang]);

  function start() {
    const rec = recRef.current;
    if (!rec) return;
    setError(null);
    try {
      rec.start();
      setListening(true);
    } catch {
      // start() throws InvalidStateError if called while already started — the
      // state is already what we want, so swallow.
      setListening(true);
    }
  }

  function stop() {
    const rec = recRef.current;
    if (!rec) return;
    try { rec.stop(); } catch { /* noop */ }
    // listening flips to false in onend; don't pre-flip here or the UI will
    // show "stopped" before the final result arrives.
  }

  function clearError() {
    setError(null);
  }

  return { supported, listening, error, start, stop, clearError };
}
